// src/hooks/useApi.ts - FIXED: Remove unused jsonError variable
import { useCallback, useEffect, useState, useRef } from 'react';
import { useAppContext } from './useAppContext';
import { useAuth } from './useAuth';
import type {
  SearchResponse,
  MetadataResponse,
  SchemaResponse,
  SearchState,
  RequestCache,
  DataSourceType,
  UserList
} from '../types';
import { API_BASE_URL, CACHE_DURATION } from '../constants';
import { requestTracker, type RequestTrackerState, type RequestType } from '../utils/requestTracker';

// FIXED: Only cache people data - no caching for contacts
const peopleRequestCache: RequestCache = {};

// Rate limit tracking
interface RateLimitInfo {
  remaining: number;
  reset: number;
  limit: number;
}

interface BackoffState {
  count: number;
  lastAttempt: number;
  backoffUntil: number;
}

// Circuit breaker for repeated failures
class CircuitBreaker {
  private failureCount = 0;
  private lastFailureTime = 0;
  private state: 'closed' | 'open' | 'half-open' = 'closed';
  
  private maxFailures: number;
  private resetTimeoutMs: number;
  
  constructor(
    maxFailures = 5,
    resetTimeoutMs = 30000
  ) {
    this.maxFailures = maxFailures;
    this.resetTimeoutMs = resetTimeoutMs;
  }
  
  canMakeRequest(): boolean {
    const now = Date.now();
    
    if (this.state === 'open') {
      if (now - this.lastFailureTime > this.resetTimeoutMs) {
        this.state = 'half-open';
        return true;
      }
      return false;
    }
    
    return true;
  }
  
  onSuccess() {
    this.failureCount = 0;
    this.state = 'closed';
  }
  
  onFailure() {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    
    if (this.failureCount >= this.maxFailures) {
      this.state = 'open';
    }
  }
  
  getState() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      canRequest: this.canMakeRequest()
    };
  }
}

// Exponential backoff manager
class BackoffManager {
  private backoffStates = new Map<string, BackoffState>();
  
  shouldWait(key: string): number {
    const state = this.backoffStates.get(key);
    if (!state) return 0;
    
    const now = Date.now();
    return Math.max(0, state.backoffUntil - now);
  }
  
  addFailure(key: string, isRateLimit = false) {
    const existing = this.backoffStates.get(key) || { count: 0, lastAttempt: 0, backoffUntil: 0 };
    const now = Date.now();
    
    // Reset if it's been a while since last failure
    if (now - existing.lastAttempt > 60000) {
      existing.count = 0;
    }
    
    existing.count++;
    existing.lastAttempt = now;
    
    // Exponential backoff: 2^count seconds, max 60 seconds
    // Rate limit gets longer backoff
    const baseDelay = isRateLimit ? 5000 : 1000;
    const delay = Math.min(baseDelay * Math.pow(2, existing.count), 60000);
    existing.backoffUntil = now + delay;
    
    this.backoffStates.set(key, existing);
    
    console.log(`⏳ Backoff for ${key}: ${delay}ms (attempt ${existing.count})`);
    return delay;
  }
  
  clearFailures(key: string) {
    this.backoffStates.delete(key);
  }
}

// Global instances
const circuitBreaker = new CircuitBreaker();
const backoffManager = new BackoffManager();

interface AuthError extends Error {
  status: number;
  isAuthError: boolean;
  isRateLimit: boolean;
  isPermissionError: boolean;
}

const createAuthError = (status: number, message: string): AuthError => {
  const error = new Error(message) as AuthError;
  error.status = status;
  error.isAuthError = status === 401;
  error.isRateLimit = status === 429;
  error.isPermissionError = status === 403;
  return error;
};

const getAuthHeaders = (apiKey: string | null): Record<string, string> => {
  if (!apiKey || apiKey === 'null' || apiKey === 'undefined') {
    throw new Error('No valid API key available for authentication');
  }
  return {
    'Authorization': `Bearer ${apiKey}`,
    'Content-Type': 'application/json'
  };
};

// Parse rate limit headers
const parseRateLimitHeaders = (headers: Headers): RateLimitInfo | null => {
  try {
    const remaining = headers.get('X-RateLimit-Remaining-Minute');
    const reset = headers.get('X-RateLimit-Reset-Minute');
    const limit = headers.get('X-RateLimit-Limit-Minute');
    
    if (remaining && reset && limit) {
      return {
        remaining: parseInt(remaining, 10),
        reset: parseInt(reset, 10),
        limit: parseInt(limit, 10)
      };
    }
  } catch (error) {
    console.warn('Failed to parse rate limit headers:', error);
  }
  return null;
};

// FIXED: Remove caching for contacts entirely
const getCacheConfig = (dataSource: DataSourceType, type: string) => {
  if (dataSource === 'contacts') {
    // FIXED: No caching for contacts data
    return {
      cache: null,
      duration: 0
    };
  } else {
    // People are relatively static - use longer cache duration
    return {
      cache: peopleRequestCache,
      duration: type === 'metadata' ? CACHE_DURATION * 5 : CACHE_DURATION
    };
  }
};

// Add debounce for auth errors to prevent spam
let lastAuthErrorTime = 0;
const AUTH_ERROR_DEBOUNCE_MS = 5000; // 5 seconds

export const useApi = () => {
  const { dispatch, showToast } = useAppContext();
  const { apiKey } = useAuth();
  
  // ... existing state and hooks ...

  // Enhanced error handling with circuit breaker and debounce
  const handleAuthError = useCallback((error: AuthError, endpoint: string) => {
    const now = Date.now();
    if (now - lastAuthErrorTime < AUTH_ERROR_DEBOUNCE_MS) {
      console.log(`Auth error debounced: ${error.message}`);
      return;
    }
    lastAuthErrorTime = now;
    
    console.error(`🔒 Authentication error for ${endpoint}:`, error);
    
    // Track failure in circuit breaker
    circuitBreaker.onFailure();
    
    if (error.isAuthError) {
      showToast({
        type: 'error',
        title: 'Authentication Failed',
        message: 'Invalid API key. Please check your credentials.',
        duration: 7000
      });
      
      dispatch({ type: 'SET_API_CONNECTED', payload: false });
      
    } else if (error.isPermissionError) {
      showToast({
        type: 'warning',
        title: 'Access Denied', 
        message: `Insufficient permissions for ${endpoint}. Contact your administrator.`,
        duration: 6000
      });
      
    } else if (error.isRateLimit) {
      const backoffTime = backoffManager.addFailure(endpoint, true);
      showToast({
        type: 'warning',
        title: 'Rate Limit Exceeded',
        message: `Too many requests. Waiting ${Math.round(backoffTime / 1000)}s before retrying.`,
        duration: 8080
      });
    }
  }, [showToast, dispatch]);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);

  // Stable tracker state management
  const [trackerState, setTrackerState] = useState<RequestTrackerState>(() => requestTracker.getState());
  const trackerStateRef = useRef(trackerState);

  // Subscribe to request tracker state changes with debouncing
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleStateChange = (newState: RequestTrackerState) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        trackerStateRef.current = newState;
        setTrackerState(newState);
      }, 50);
    };

    const unsubscribe = requestTracker.subscribe(handleStateChange);
    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  // Enhanced error handling with circuit breaker
  const makeRequest = useCallback(async <T>(
    url: string,
    options: RequestInit = {},
    cacheKey?: string,
    type?: RequestType,
    fields?: string[],
    dataSource: DataSourceType = 'people'
  ): Promise<{ data: T; requestId: string } | null> => {
    
    // Check circuit breaker
    if (!circuitBreaker.canMakeRequest()) {
      const breakerState = circuitBreaker.getState();
      console.warn(`🚫 Circuit breaker ${breakerState.state} - preventing request to ${url}`);
      throw new Error('Service temporarily unavailable (circuit breaker open)');
    }
    
    // Check backoff
    const backoffKey = `${type || 'unknown'}-${url}`;
    const waitTime = backoffManager.shouldWait(backoffKey);
    if (waitTime > 0) {
      console.log(`⏳ Backing off ${url} for ${waitTime}ms`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    // FIXED: Special handling for owner_id field - never cache
    const isOwnerField = fields?.includes('owner_id');
    const shouldSkipCache = isOwnerField && dataSource === 'contacts';
    
    // FIXED: Check cache with proper null handling
    if (cacheKey && type && !shouldSkipCache) {
      const { cache } = getCacheConfig(dataSource, type);
      
      if (cache && cache[cacheKey]) {
        const cached = cache[cacheKey];
        if (Date.now() < cached.expiry) {
          console.log(`📋 Cache hit for ${dataSource}: ${cacheKey}`);
          return { data: cached.data as T, requestId: 'cached' };
        }
      }
    }

    // Start tracking request with differentiated types
    const trackedRequest = type ? requestTracker.startRequest(type, fields) : null;
    const signal = trackedRequest?.abortController.signal;

    try {
      if (!apiKey || apiKey === 'null' || apiKey === 'undefined') {
        console.warn('⚠️ No valid API key available for request, skipping...');
        return null;
      }
      
      const authHeaders = getAuthHeaders(apiKey);
      
      const response = await fetch(url, {
        ...options,
        headers: {
          ...authHeaders,
          ...options.headers
        },
        signal
      });

      // Check if request was cancelled after fetch
      if (signal?.aborted) {
        console.log(`⏹️ Request cancelled: ${url}`);
        return null;
      }

      // Parse and store rate limit info
      const newRateLimitInfo = parseRateLimitHeaders(response.headers);
      if (newRateLimitInfo) {
        setRateLimitInfo(newRateLimitInfo);
      }

      // Handle errors with enhanced logic
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch {
          // Fall back to status text if JSON parsing fails
        }

        // Create appropriate error type
        if (response.status === 401) {
          const authError = createAuthError(401, 'Invalid or missing API key');
          handleAuthError(authError, url);
          throw authError;
          
        } else if (response.status === 403) {
          const permError = createAuthError(403, errorMessage);
          handleAuthError(permError, url);
          throw permError;
          
        } else if (response.status === 429) {
          const rateError = createAuthError(429, 'Rate limit exceeded');
          handleAuthError(rateError, url);
          throw rateError;
          
        } else {
          // Track other failures for circuit breaker
          backoffManager.addFailure(backoffKey, false);
          throw new Error(errorMessage);
        }
      }

      // FIXED: Handle 204 No Content responses
      let data: T;
      const contentType = response.headers.get('content-type') || '';
      
      if (response.status === 204 || !contentType.includes('application/json')) {
        // No content response or non-JSON response
        data = { success: true } as T;
      } else {
        try {
          data = await response.json();
        } catch {
          // If we can't parse JSON but response was successful, return success
          if (response.ok) {
            data = { success: true } as T;
          } else {
            throw new Error('Invalid JSON response from server');
          }
        }
      }

      // Double-check cancellation before processing data
      if (signal?.aborted) {
        console.log(`⏹️ Request cancelled after response: ${url}`);
        return null;
      }

      if (data === null || data === undefined) {
        throw new Error('Empty response from server');
      }

      // Mark success in circuit breaker and clear backoff
      circuitBreaker.onSuccess();
      backoffManager.clearFailures(backoffKey);

      if (!trackerState.isSearching) {
        dispatch({ type: 'SET_API_CONNECTED', payload: true });
      }

      // FIXED: Cache successful responses only for people data
      if (cacheKey && !signal?.aborted && type && !shouldSkipCache) {
        const { cache, duration } = getCacheConfig(dataSource, type);
        
        if (cache && duration > 0) {
          cache[cacheKey] = {
            data,
            timestamp: Date.now(),
            expiry: Date.now() + duration
          };
          console.log(`💾 Cached response for ${dataSource}: ${cacheKey} (${duration}ms)`);
        }
      }

      // Mark request as completed
      if (trackedRequest && !signal?.aborted) {
        requestTracker.completeRequest(trackedRequest.id, 'completed');
      }

      return { data, requestId: trackedRequest?.id || 'untracked' };
      
    } catch (error) {
      if (trackedRequest) {
        if (error instanceof Error && (error.message === 'Request was cancelled' || error.name === 'AbortError')) {
          console.log(`⏹️ Request cancelled: ${trackedRequest.id}`);
          return null;
        } else {
          requestTracker.completeRequest(trackedRequest.id, 'failed');
        }
      }

      if (error instanceof Error && error.name === 'AbortError') {
        console.log(`⏹️ Request aborted: ${url}`);
        return null;
      }

      const authError = error as AuthError;
      if (!authError.isAuthError && !authError.isPermissionError && !authError.isRateLimit) {
        console.error(`❌ Request failed for ${url}:`, error);
      }
      
      throw error;
    }
  }, [handleAuthError, dispatch, trackerState.isSearching, apiKey]);

  // Health check with retry logic
  const checkApiHealth = useCallback(async (): Promise<boolean> => {
    // Don't attempt health check if no API key is available
    if (!apiKey || apiKey === 'null' || apiKey === 'undefined') {
      console.log('⚠️ Skipping API health check - no API key available');
      return false;
    }

    try {
      console.log('🔍 Checking API health with authentication...');
      
      // Check if we should wait due to rate limiting
      const waitTime = backoffManager.shouldWait('health');
      if (waitTime > 0) {
        console.log(`⏳ Waiting ${waitTime}ms before health check due to rate limiting`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      
      const result = await makeRequest(`${API_BASE_URL}/health`, {}, 'health', 'health');
      if (result) {
        console.log('✅ API health check passed with authentication');
        return true;
      }
      return false;
    } catch (error) {
      const authError = error as AuthError;
      
      if (authError.isPermissionError) {
        console.log('⚠️ Health endpoint requires special permissions, checking root endpoint...');
        try {
          // Add a small delay before trying root endpoint
          await new Promise(resolve => setTimeout(resolve, 1000));
          const rootResult = await makeRequest(`${API_BASE_URL}/`, {}, 'root_health', 'health');
          if (rootResult) {
            console.log('✅ API connection verified via root endpoint');
            return true;
          }
        } catch (rootError) {
          console.error('❌ Root endpoint also failed:', rootError);
        }
      }
      
      console.error('❌ API health check failed:', error);
      return false;
    }
  }, [makeRequest, apiKey]);

  // Schema loading with production-ready error handling
  const getSchema = useCallback(async (): Promise<SchemaResponse | null> => {
    try {
      console.log('📋 Loading schema with authentication...');
      
      // Check rate limit before making request
      if (rateLimitInfo && rateLimitInfo.remaining < 5) {
        const waitTime = Math.max(0, rateLimitInfo.reset - Date.now());
        if (waitTime > 0) {
          console.log(`⏳ Rate limit low (${rateLimitInfo.remaining}), waiting ${waitTime}ms`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
      
      const result = await makeRequest<SchemaResponse>(
        `${API_BASE_URL}/api/schema`,
        {},
        'schema',
        'schema'
      );

      if (!result) return null;

      const { data } = result;
      console.log('🔍 Schema response:', {
        success: data.success,
        hasFieldDefinitions: !!data.field_definitions,
        hasSections: !!data.sections,
        hasColumnConfigs: !!data.column_configs,
        columnConfigsLength: data.column_configs?.length || 0,
        columnConfigs: data.column_configs?.slice(0, 3) || [] // Show first 3 for debugging
      });
      
      if (!data.success || !data.field_definitions || !data.sections || !data.column_configs) {
        throw new Error('Invalid schema response structure');
      }

      console.log('✅ Schema loaded successfully with authentication');
      return data;
    } catch (error) {
      const authError = error as AuthError;
      
      if (authError.isAuthError || authError.isPermissionError) {
        console.error('🔒 Schema loading failed due to authentication/permission error');
        return null;
      }
      
      console.error('❌ Schema request failed:', error);
      return null;
    }
  }, [makeRequest, rateLimitInfo]);

  // PRODUCTION-READY: Enhanced keyword suggestions loading
  const loadKeywordSuggestions = useCallback(async (
    dataSource: DataSourceType = 'people',
    requestType: RequestType = 'schema'
  ): Promise<void> => {
    try {
      console.log(`🔄 Loading keyword suggestions for ${dataSource}...`);
      
      // Add a delay to avoid rapid-fire requests during initialization
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const result = await makeRequest<{
        success: boolean;
        suggestions: Record<string, string[]>;
        total_fields: number;
        cache_status: string;
        data_source: DataSourceType;
      }>(
        `${API_BASE_URL}/api/keyword_suggestions?data_source=${dataSource}`,
        {},
        `keyword_suggestions_${dataSource}`,
        requestType,
        undefined,
        dataSource
      );

      if (!result) return;

      const { data } = result;
      if (data.success && data.suggestions) {
        dispatch({ 
          type: 'SET_KEYWORD_SUGGESTIONS', 
          payload: data.suggestions
        });
        console.log(`✅ Loaded keyword suggestions for ${data.total_fields} fields from ${dataSource}`);
      }
    } catch (error) {
      const authError = error as AuthError;
      
      if (authError.isPermissionError) {
        console.log('⚠️ Keyword suggestions require special permissions, skipping...');
        dispatch({ type: 'SET_KEYWORD_SUGGESTIONS', payload: {} });
        return;
      }
      
      if (authError.isRateLimit) {
        console.log('⚠️ Rate limited loading keyword suggestions, using empty suggestions');
        dispatch({ type: 'SET_KEYWORD_SUGGESTIONS', payload: {} });
        return;
      }
      
      console.error('❌ Failed to load keyword suggestions:', error);
      dispatch({ type: 'SET_KEYWORD_SUGGESTIONS', payload: {} });
    }
  }, [makeRequest, dispatch]);

  // PRODUCTION-READY: Enhanced metadata loading with comprehensive error handling
  const getMetadata = useCallback(async (
    query?: string,
    filters?: Record<string, unknown>,
    metadataFields?: string[],
    dataSource: DataSourceType = 'people',
    requestType: string = 'metadata'
  ): Promise<string | null> => {
    try {
      dispatch({ type: 'SET_METADATA_LOADING', payload: true });

      const requestBody: Record<string, unknown> = {
        data_source: dataSource
      };

      // Convert query to filters if provided
      const combinedFilters = { ...filters };
      if (query && query.trim()) {
        combinedFilters.FIRST_NAME = {
          type: 'keyword_group',
          groups: [{
            mode: 'OR',
            keywords: [query.trim()],
            match_mode: 'like'
          }]
        };
      }

      if (Object.keys(combinedFilters).length > 0) {
        requestBody.filters = combinedFilters;
      }

      requestBody.metadata_fields = metadataFields || [];

      const cacheKey = `metadata_${dataSource}_${JSON.stringify({
        query,
        filters: requestBody.filters,
        fields: metadataFields
      })}`;

      console.log(`📊 Loading metadata for ${dataSource}...`);
      
      const isOwnerFieldRequest = metadataFields?.includes('owner_id');
      if (isOwnerFieldRequest && dataSource === 'contacts') {
        console.log('🔑 Loading owner_id metadata for contacts (no cache)');
      }
      
      const result = await makeRequest<MetadataResponse>(
        `${API_BASE_URL}/api/search_metadata`,
        {
          method: 'POST',
          body: JSON.stringify(requestBody)
        },
        cacheKey,
        requestType as RequestType,
        metadataFields,
        dataSource
      );

      if (!result) {
        return null;
      }

      const { data, requestId } = result;

      if (!data.field_statistics || typeof data.field_statistics !== 'object') {
        throw new Error('Invalid metadata response structure');
      }

      console.log(`✅ Metadata loaded: ${Object.keys(data.field_statistics).length} fields from ${dataSource} [${requestId}]`);
      dispatch({ type: 'SET_METADATA', payload: data });

      return requestId;
    } catch (error) {
      const authError = error as AuthError;
      
      if (authError.isAuthError || authError.isPermissionError) {
        console.error('🔒 Metadata loading failed due to authentication/permission error');
        dispatch({ type: 'SET_METADATA_ERROR', payload: 'Authentication required for metadata access' });
        return null;
      }
      
      if (authError.isRateLimit) {
        console.log('⚠️ Rate limited loading metadata, will retry later');
        return null;
      }
      
      console.error('❌ Metadata request failed:', error);
      dispatch({ type: 'SET_METADATA_ERROR', payload: error instanceof Error ? error.message : 'Metadata request failed' });
      return null;
    } finally {
      if (!requestTracker.hasActiveRequestOfType('metadata')) {
        dispatch({ type: 'SET_METADATA_LOADING', payload: false });
      }
    }
  }, [makeRequest, dispatch]);

  // PRODUCTION-READY: Enhanced search with robust error handling
  const searchContacts = useCallback(async (
    searchParams: SearchState,
    pageSize: number,
    schema?: SchemaResponse
  ): Promise<{ response: SearchResponse; requestId: string } | null> => {
    const sortingArray = Array.isArray(searchParams.sorting) ? searchParams.sorting : [];

    let actualSortField: string | undefined;
    let actualSortOrder: string | undefined;
    
    if (sortingArray.length > 0 && schema) {
      const columnId = sortingArray[0].id;
      const columnConfig = schema.column_configs?.find((col) => col.id === columnId);
      if (columnConfig) {
        actualSortField = columnConfig.sort_field;
        actualSortOrder = sortingArray[0].desc ? 'desc' : 'asc';
      }
    }

    const cleanFilters: Record<string, unknown> = {};
    if (searchParams.filters) {
      for (const [key, value] of Object.entries(searchParams.filters)) {
        if (value !== null &&
          value !== undefined &&
          !(Array.isArray(value) && value.length === 0) &&
          !(typeof value === 'string' && value.trim() === '')) {
          cleanFilters[key] = value;
        }
      }
    }

    // Convert search query to keyword_group filter
    if (searchParams.query && searchParams.query.trim()) {
      const keywordGroupFilter = {
        type: 'keyword_group',
        groups: [{
          mode: 'OR',
          keywords: [searchParams.query.trim()],
          match_mode: 'like'
        }]
      };
      cleanFilters.FIRST_NAME = keywordGroupFilter;
    }

    const requestBody: Record<string, unknown> = {
      query: "",
      page: searchParams.page,
      size: pageSize,
      filters: Object.keys(cleanFilters).length > 0 ? cleanFilters : undefined,
      data_source: searchParams.dataSource
    };

    if (actualSortField && actualSortOrder) {
      requestBody.sort_field = actualSortField;
      requestBody.sort_order = actualSortOrder;
    }

    try {
      console.log(`🔍 Searching ${searchParams.dataSource}...`);
      const result = await makeRequest<SearchResponse>(
        `${API_BASE_URL}/api/search`,
        {
          method: 'POST',
          body: JSON.stringify(requestBody)
        },
        undefined,
        'search',
        undefined,
        searchParams.dataSource
      );

      if (!result) {
        console.log('🚫 Search request was cancelled');
        return null;
      }

      const { data, requestId } = result;

      if (!data.hits || !Array.isArray(data.hits)) {
        throw new Error('Invalid search response: hits must be an array');
      }

      if (typeof data.total !== 'number') {
        throw new Error('Invalid search response: total must be a number');
      }

      console.log(`✅ Search completed: ${data.hits.length}/${data.total} results in ${data.query_time_ms}ms [${requestId}]`);
      return { response: data, requestId };
      
    } catch (error) {
      if (error instanceof Error && (error.message === 'Request cancelled' || error.name === 'AbortError')) {
        console.log('🚫 Search request was cancelled');
        return null;
      }
      
      const authError = error as AuthError;
      if (authError.isAuthError || authError.isPermissionError) {
        console.error('🔒 Search failed due to authentication/permission error');
        throw new Error('Authentication required for search access');
      }
      
      throw error;
    }
  }, [makeRequest]);

  // Background search that doesn't interfere with main search
  const searchContactsBackground = useCallback(async (
    searchParams: SearchState,
    pageSize: number
  ): Promise<{ response: SearchResponse; requestId: string } | null> => {
    const result = await makeRequest<SearchResponse>(
      `${API_BASE_URL}/api/search`,
      {
        method: 'POST',
        body: JSON.stringify({
          query: searchParams.query || undefined,
          page: searchParams.page,
          size: pageSize,
          filters: Object.keys(searchParams.filters || {}).length > 0 ? searchParams.filters : undefined,
          data_source: searchParams.dataSource
        })
      },
      undefined,
      'background_search',
      undefined,
      searchParams.dataSource
    );

    if (!result) return null;
    return { response: result.data, requestId: result.requestId };
  }, [makeRequest]);

  // Background count loading
  const loadDataSourceCount = useCallback(async (dataSource: DataSourceType): Promise<number | null> => {
    try {
      console.log(`🔢 Loading count for ${dataSource} data source...`);
      
      const searchParams = {
        query: '',
        page: 1,
        sorting: [],
        filters: {},
        dataSource
      };

      const result = await searchContactsBackground(searchParams, 1);
      if (result) {
        console.log(`✅ ${dataSource} count loaded: ${result.response.total.toLocaleString()}`);
        return result.response.total;
      }
      return null;
    } catch (error) {
      console.error(`❌ Failed to load ${dataSource} count:`, error);
      return null;
    }
  }, [searchContactsBackground]);

  // Initial data loading with proper sequencing
  const loadInitialMetadata = useCallback(async (): Promise<void> => {
    try {
      console.log('🔄 Loading initial data concurrently...');
      
      const promises = [
        getMetadata(undefined, undefined, undefined, 'people', 'initial-metadata-people'),
        getMetadata(undefined, undefined, undefined, 'contacts', 'initial-metadata-contacts'),
        loadKeywordSuggestions('people', 'initial-suggestions-people'),
        loadKeywordSuggestions('contacts', 'initial-suggestions-contacts')
      ];
      
      const results = await Promise.allSettled(promises);
      
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`❌ Initial load request failed for promise at index ${index}:`, result.reason);
        }
      });

    } catch (error) {
      console.error('❌ Failed to load initial data:', error);
    }
  }, [getMetadata, loadKeywordSuggestions]);

  // PRODUCTION-READY: List management functions
  const getLists = useCallback(async (
    include_contact_counts: boolean = true
  ) => {
    if (requestTracker.hasActiveRequestOfType('get_lists')) {
      console.log('🚫 Another get_lists request is already in progress. Skipping.');
      return null;
    }
    
    try {
      console.log('📋 Loading user lists...');
      
      const requestBody = {
        include_contact_counts
      };
      
      const result = await makeRequest(
        `${API_BASE_URL}/api/lists/get`,
        {
          method: 'POST',
          body: JSON.stringify(requestBody)
        },
        undefined,
        'get_lists'
      );
      
      if (!result) {
        console.log('🚫 Get lists request was cancelled');
        return null;
      }
      
      const { data } = result;
      const responseData = data as { lists?: UserList[] };
      console.log(`✅ Lists loaded: ${responseData.lists?.length || 0} lists`);
      
      return responseData;
    } catch (error) {
      console.error('❌ Get lists failed:', error);
      throw error;
    }
  }, [makeRequest]);

  const createList = useCallback(async (
    name: string,
    description?: string,
    tags?: string[],
    is_default?: boolean
  ) => {
    try {
      const requestBody = {
        name,
        description,
        tags: tags || [],
        is_default: is_default || false
      };
      
      const result = await makeRequest(
        `${API_BASE_URL}/api/lists/create`,
        {
          method: 'POST',
          body: JSON.stringify(requestBody)
        },
        undefined,
        'create_list'
      );
      
      if (!result) {
        return null;
      }
      
      return result.data;
    } catch (error) {
      console.error('❌ Create list failed:', error);
      throw error;
    }
  }, [makeRequest]);

  const deleteList = useCallback(async (list_id: string) => {
    try {
      const result = await makeRequest(
        `${API_BASE_URL}/api/lists/delete`,
        {
          method: 'POST',
          body: JSON.stringify({ list_id })
        },
        undefined,
        'delete_list'
      );
      return result?.data;
    } catch (error) {
      console.error('❌ Delete list failed:', error);
      throw error;
    }
  }, [makeRequest]);

  // PRODUCTION-READY: Admin API functions for user management
  const createUser = useCallback(async (
    name: string,
    permissions: string[],
    email?: string
  ) => {
    try {
      const result = await makeRequest(
        `${API_BASE_URL}/api/admin/users`,
        {
          method: 'POST',
          body: JSON.stringify({ name, permissions, email: email || undefined })
        },
        undefined,
        'create_user'
      );
      return result?.data;
    } catch (error) {
      console.error('❌ Create user failed:', error);
      throw error;
    }
  }, [makeRequest]);

  const listUsers = useCallback(async (page: number = 1, size: number = 50) => {
    try {
      const result = await makeRequest(
        `${API_BASE_URL}/api/admin/users/search?page=${page}&size=${size}`,
        { method: 'POST' },
        undefined,
        'list_users'
      );
      return result?.data;
    } catch (error) {
      console.error('❌ List users failed:', error);
      throw error;
    }
  }, [makeRequest]);

  const getUser = useCallback(async (userApiKey: string) => {
    try {
      const result = await makeRequest(
        `${API_BASE_URL}/api/admin/users/${userApiKey}`,
        { method: 'GET' },
        undefined,
        'get_user'
      );
      return result?.data;
    } catch (error) {
      console.error('❌ Get user failed:', error);
      throw error;
    }
  }, [makeRequest]);

  const updateUser = useCallback(async (
    userApiKey: string,
    updates: { name?: string; permissions?: string[]; is_active?: boolean }
  ) => {
    try {
      const result = await makeRequest(
        `${API_BASE_URL}/api/admin/users/${userApiKey}`,
        {
          method: 'PUT',
          body: JSON.stringify(updates)
        },
        undefined,
        'update_user'
      );
      return result?.data;
    } catch (error) {
      console.error('❌ Update user failed:', error);
      throw error;
    }
  }, [makeRequest]);

  // FIXED: Handle 204 responses properly for delete operations
  const deleteUserAdmin = useCallback(async (userApiKey: string) => {
    try {
      const result = await makeRequest(
        `${API_BASE_URL}/api/admin/users/${userApiKey}`,
        { method: 'DELETE' },
        undefined,
        'delete_user'
      );
      return result?.data;
    } catch (error) {
      console.error('❌ Delete user failed:', error);
      throw error;
    }
  }, [makeRequest]);

  // System reset function for admin panel
  const systemReset = useCallback(async (confirmationPhrase: string) => {
    try {
      const result = await makeRequest(
        `${API_BASE_URL}/api/admin/system/reset`,
        {
          method: 'POST',
          body: JSON.stringify({ confirm: confirmationPhrase })
        },
        undefined,
        'system_reset'
      );
      return result?.data;
    } catch (error) {
      console.error('❌ System reset failed:', error);
      throw error; // Re-throw to be handled by the component
    }
  }, [makeRequest]);

  return {
    searchContacts,
    searchContactsBackground,
    getMetadata,
    loadKeywordSuggestions,
    loadInitialMetadata,
    loadDataSourceCount,
    refreshMetadata: getMetadata,
    createList,
    getLists,
    deleteList,
    // Admin functions
    createUser,
    listUsers,
    getUser,
    updateUser,
    deleteUserAdmin,
    systemReset,
    requestTrackerState: trackerState,
    circuitBreakerState: circuitBreaker.getState(),
    rateLimitInfo,
    checkApiHealth,
    getSchema
  };
};

export default useApi;